import * as React from 'react';
import { Edit3, Loader2, Plus, Trash2, UploadCloud } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ImportButton from 'core/components/ImportButton/ImportButton';
import { showGlobalModal } from 'core/components/GlobalModal';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import httpclient from 'libs/Axios';
import avatarPlaceholder from 'assets/icons/Avatar.svg';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { DigitalMarketingDeleteModal } from 'features/DigitalMarketing/components/DigitalMarketingDeleteModal';
import { ManagementFilterBar } from 'features/DigitalMarketing/components/ManagementFilterBar';
import {
  ManagementTable,
  type ManagementTableColumn,
  type ManagementTableRowId,
} from 'features/DigitalMarketing/components/ManagementTable';
import {
  buildEditWebsiteContentPath,
  EDIT_WEBSITE_CONTENT_BASE_PATH,
} from 'features/DigitalMarketing/routes/editWebsiteContentPaths';
import { getApiErrorMessage } from 'features/DigitalMarketing/utils/apiResponse';
import { LeadershipProfileModal } from 'features/DigitalMarketing/pages/EditWebsiteContent/LeadershipProfiles/LeadershipProfileModal';
import { PaginationBar } from 'core/components/PaginationBar';
import {
  DMButton,
  DMDiv,
  DMImage,
  DMSelect,
  DMText,
  DMOption,
} from '../../../components/Primitives';

type LeadershipStatus = 'Publish' | 'Unpublish' | 'N/A';

export type LeadershipProfile = {
  id: number;
  founderId?: number;
  founderIndex: number;
  staffId?: number;
  image: string;
  facultyName: string;
  qualification: string;
  experience: string;
  gender: string;
  email: string;
  countryCode: string;
  mobile: string;
  role: string;
  about: string;
  status: LeadershipStatus;
  rawStaff?: any;
};

const statusClassName: Record<LeadershipStatus, string> = {
  Publish: 'bg-emerald-100 text-emerald-600',
  Unpublish: 'bg-amber-100 text-amber-600',
  'N/A': 'bg-gray-100 text-gray-500',
};

const tableSkeletonRows = Array.from({ length: 8 });
const normalizeProfileStatus = (status?: string): LeadershipStatus => {
  const normalizedStatus = String(status || '')
    .trim()
    .toUpperCase();

  if (!normalizedStatus) {
    return 'N/A';
  }

  return normalizedStatus === 'UNPUBLISH' ? 'Unpublish' : 'Publish';
};

type LeadershipFilter = 'all' | LeadershipStatus;

const leadershipFilterOptions = [
  { label: 'All Profiles', value: 'all' as LeadershipFilter },
  { label: 'Published Only', value: 'Publish' as LeadershipFilter },
  { label: 'Unpublished Only', value: 'Unpublish' as LeadershipFilter },
];

type ProfileResource = 'profile' | 'staff' | 'faculty';

type ProfileApiConfig = {
  resource?: ProfileResource;
  list: string;
  listQuery?: string;
  delete: string;
  publish?: string;
  indexParam: string;
};

export type ProfileFlowConfig = {
  title: string;
  description: string;
  listSubPath: string;
  createSubPath: string;
  editSubPath: string;
  emptyMessage: string;
  nameColumnLabel: string;
  breadcrumbLabel: string;
  showRoleColumn?: boolean;
  api: ProfileApiConfig;
};

export const leadershipProfileFlowConfig: ProfileFlowConfig = {
  title: 'Leadership Profiles',
  description:
    "Showcase leadership profiles on institution's website- Founder, Co-Founder, Principal",
  listSubPath: 'leadership-profile-list',
  createSubPath: 'leadership-profile-list/create',
  editSubPath: 'leadership-profile-list/edit',
  emptyMessage: 'No leadership profiles found',
  nameColumnLabel: 'Founder Name',
  breadcrumbLabel: 'Leadership Profiles',
  showRoleColumn: true,
  api: {
    resource: 'profile',
    list: '/master/listFounders',
    delete: '/master/deleteFounder',
    publish: '/master/publishFounder',
    indexParam: 'founder_id',
  },
};

const mapFounderToProfile = (item: any, index: number): LeadershipProfile => ({
  id: item?.founder_id || index,
  founderId: item?.founder_id,
  founderIndex: index,
  image: item?.image_url || item?.Image_url || item?.Image_URL || '',
  facultyName: item?.founder_name || item?.Name || '',
  qualification: item?.qualification || item?.Qualification || '',
  experience: String(item?.experience || item?.Experience || ''),
  gender: item?.gender || item?.Gender || '',
  email: item?.email_id || item?.Email_id || '',
  countryCode: item?.country_code || item?.Country_code || '',
  mobile: item?.mobile_number || item?.Mobile_Number || '',
  role: item?.role || item?.Role || '',
  about: item?.description || item?.Description || '',
  status:
    typeof item?.is_web_published === 'boolean'
      ? item.is_web_published
        ? 'Publish'
        : 'Unpublish'
      : normalizeProfileStatus(item?.STATUS || item?.Status || item?.status),
});

const getStaffName = (item: any) =>
  item?.STAFF_NAME ||
  [item?.FIRST_NAME, item?.MIDDLE_NAME, item?.LAST_NAME]
    .filter(Boolean)
    .join(' ') ||
  '';

const getStaffRoleNames = (item: any) =>
  item?.STAFF_ROLES?.map(
    (role: any) =>
      role?.BRANCH_ROLE_NAME || role?.BranchRole?.NAME || role?.NAME
  )
    ?.filter(Boolean)
    ?.join(', ') ||
  item?.BRANCH_ROLE_NAME ||
  '';

const mapStaffToProfile = (item: any, index: number): LeadershipProfile => ({
  id: item?.STAFF_ID || index,
  founderIndex: index,
  staffId: item?.STAFF_ID,
  image:
    item?.PHOTO_URL ||
    item?.Image_url ||
    item?.Image_URL ||
    item?.image_url ||
    '',
  facultyName: getStaffName(item),
  qualification: item?.QUALIFICATION || '',
  experience: String(item?.TEACHING_SINCE || ''),
  gender: item?.GENDER || '',
  email: item?.EMAIL_ID || '',
  countryCode: item?.COUNTRY_CODE || '',
  mobile: item?.MOBILE_NUMBER || '',
  role: getStaffRoleNames(item),
  about: item?.DESCRIPTION || item?.ADDRESS || '',
  status: normalizeProfileStatus(item?.STATUS || item?.Status || item?.status),
  rawStaff: item,
});

const mapFacultyToProfile = (item: any, index: number): LeadershipProfile => ({
  id: item?.staff_id || index,
  founderIndex: index,
  staffId: item?.staff_id,
  image: item?.photo_url || '',
  facultyName: [item?.first_name, item?.middle_name, item?.last_name]
    .filter(Boolean)
    .join(' '),
  qualification: item?.qualification || '',
  experience: String(item?.teaching_since || ''),
  gender: item?.gender || '',
  email: item?.email_id || '',
  countryCode: item?.country_code || '',
  mobile: item?.mobile_number || '',
  role: '',
  about: item?.description || '',
  status:
    typeof item?.is_web_published === 'boolean'
      ? item.is_web_published
        ? 'Publish'
        : 'Unpublish'
      : normalizeProfileStatus(item?.status),
  rawStaff: item,
});

type LeadershipProfileListProps = {
  config?: ProfileFlowConfig;
};

export function LeadershipProfileList({
  config = leadershipProfileFlowConfig,
}: LeadershipProfileListProps) {
  const navigate = useNavigate();
  const authData = useAuth();
  const [profileRows, setProfileRows] = React.useState<LeadershipProfile[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<ManagementTableRowId[]>(
    []
  );
  const [selectedProfile, setSelectedProfile] =
    React.useState<LeadershipProfile | null>(null);
  const [deleteProfile, setDeleteProfile] =
    React.useState<LeadershipProfile | null>(null);
  const [deleteStep, setDeleteStep] = React.useState<'idle' | 'confirm'>(
    'idle'
  );
  const [statusFilter, setStatusFilter] =
    React.useState<LeadershipFilter>('all');
  const [isLoadingProfiles, setIsLoadingProfiles] = React.useState(false);
  const [isPublishingProfiles, setIsPublishingProfiles] = React.useState(false);
  const [publishingProfileId, setPublishingProfileId] = React.useState<
    number | null
  >(null);
  const [deletingProfileId, setDeletingProfileId] = React.useState<
    number | null
  >(null);
  const [pageIndex, setPageIndex] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [totalRecords, setTotalRecords] = React.useState(0);

  const getStaffListUrl = React.useCallback(() => {
    const baseUrl = `${config.api.list}/${authData?.BRANCH_ID}`;

    return config.api.listQuery
      ? `${baseUrl}?${config.api.listQuery}`
      : baseUrl;
  }, [authData?.BRANCH_ID, config.api.list, config.api.listQuery]);

  const fetchProfiles = React.useCallback(
    async (currentPage: number, currentSize: number) => {
      if (config.api.resource === 'staff' && !authData?.BRANCH_ID) {
        return;
      }

      setIsLoadingProfiles(true);

      try {
        // Determine base URL depending on the resource type
        const baseUrl =
          config.api.resource === 'staff' ? getStaffListUrl() : config.api.list;

        // Safely append pagination parameters to the URL
        const separator = baseUrl.includes('?') ? '&' : '?';
        const url = `${baseUrl}${separator}page_size=${currentSize}&page_index=${currentPage}`;

        const response = await httpclient().get(url);
        const responseData = response?.data?.data || {};

        // Dynamically find the array inside the response (e.g., 'Faculty', 'StaffList', etc.)
        const arrayKey = Object.keys(responseData).find(key =>
          Array.isArray(responseData[key])
        );
        const itemsArray = arrayKey
          ? responseData[arrayKey]
          : Array.isArray(responseData)
          ? responseData
          : [];

        // Look for the Total count, fallback to array length if not found
        const currentTotalRecords =
          responseData.Total ||
          responseData.total_count ||
          itemsArray.length ||
          0;
        setTotalRecords(currentTotalRecords);

        // Map the specific profile type based on the resource config
        if (config.api.resource === 'staff') {
          setProfileRows(itemsArray.map(mapStaffToProfile));
        } else if (config.api.resource === 'faculty') {
          setProfileRows(itemsArray.map(mapFacultyToProfile));
        } else {
          setProfileRows(itemsArray.map(mapFounderToProfile));
        }

        setSelectedIds([]);
      } catch (error: any) {
        showGlobalModal(
          getApiErrorMessage(
            error,
            `Unable to fetch ${config.title.toLowerCase()}`
          ),
          'error'
        );
        setProfileRows([]);
        setTotalRecords(0);
      } finally {
        setIsLoadingProfiles(false);
      }
    },
    [
      authData?.BRANCH_ID,
      config.api.list,
      config.api.resource,
      config.title,
      getStaffListUrl,
    ]
  );

  React.useEffect(() => {
    fetchProfiles(pageIndex, pageSize);
  }, [fetchProfiles, pageIndex, pageSize]);

  const visibleProfiles = React.useMemo(
    () =>
      statusFilter === 'all'
        ? profileRows
        : profileRows.filter(item => item.status === statusFilter),
    [profileRows, statusFilter]
  );

  const handleSelectAll = (
    ids: ManagementTableRowId[],
    allSelected: boolean
  ) => {
    setSelectedIds(current => {
      if (allSelected) {
        return current.filter(id => !ids.includes(id));
      }

      return Array.from(new Set([...current, ...ids]));
    });
  };

  const handleSelectRow = (id: ManagementTableRowId) => {
    const row = profileRows.find(profile => profile.id === id);
    if (
      (config.api.resource === 'profile' ||
        config.api.resource === 'faculty') &&
      row?.status === 'Publish'
    ) {
      return;
    }

    setSelectedIds(current =>
      current.includes(id)
        ? current.filter(item => item !== id)
        : [...current, id]
    );
  };

  const handleDeleteRequest = React.useCallback(
    (profile: LeadershipProfile) => {
      setDeleteProfile(profile);
      setDeleteStep('confirm');
    },
    []
  );

  const handleFilterChange = (filter: LeadershipFilter) => {
    setStatusFilter(filter);
  };

  const handleDeleteSuccess = async (profile: LeadershipProfile) => {
    setDeletingProfileId(profile.id);

    try {
      const deleteUrl =
        config.api.resource === 'staff'
          ? `${config.api.delete}?id=${profile.staffId}&institute_id=${authData?.INSTITUTE_ID}&branch_id=${authData?.BRANCH_ID}`
          : `${config.api.delete}/${
              profile.founderId ?? profile.staffId ?? profile.id
            }`;
      const response = await httpclient().delete(deleteUrl);
      setProfileRows(current => current.filter(item => item.id !== profile.id));
      setSelectedIds(current => current.filter(id => id !== profile.id));
      setSelectedProfile(current =>
        current?.id === profile.id ? null : current
      );
      showGlobalModal(
        response?.data?.message || 'Profile deleted successfully'
      );
      fetchProfiles(pageIndex, pageSize);
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to delete profile'),
        'error'
      );
    } finally {
      setDeletingProfileId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteProfile) {
      return;
    }

    await handleDeleteSuccess(deleteProfile);
    setDeleteStep('idle');
    setDeleteProfile(null);
  };

  const handlePublishProfiles = async () => {
    if (
      config.api.resource === 'profile' ||
      config.api.resource === 'faculty'
    ) {
      if (!config.api.publish) {
        return;
      }

      if (!selectedIds.length) {
        showGlobalModal('Select at least one profile to publish.', 'error');
        return;
      }

      const unpublishedSelectedProfiles = profileRows.filter(
        profile =>
          selectedIds.includes(profile.id) && profile.status !== 'Publish'
      );

      if (!unpublishedSelectedProfiles.length) {
        showGlobalModal('Selected profiles are already published.', 'warning');
        return;
      }

      setIsPublishingProfiles(true);

      try {
        await Promise.all(
          unpublishedSelectedProfiles.map(profile =>
            httpclient().post(
              `${config.api.publish}/${
                profile.founderId ?? profile.staffId ?? profile.id
              }?is_web_published=true`
            )
          )
        );

        showGlobalModal('Published successfully');
        await fetchProfiles(pageIndex, pageSize);
      } catch (error: any) {
        showGlobalModal(
          getApiErrorMessage(error, 'Failed to publish profiles'),
          'error'
        );
      } finally {
        setIsPublishingProfiles(false);
      }
      return;
    }

    if (config.api.resource !== 'staff') {
      return;
    }

    if (selectedIds.length < 3 || selectedIds.length > 5) {
      showGlobalModal(
        'Selection must include at least 3 and no more than 5 items to publish.',
        'error'
      );
      return;
    }

    setIsPublishingProfiles(true);

    try {
      const response = await httpclient().post('data/branch/staffPublish', {
        STAFF_IDS: selectedIds,
      });

      showGlobalModal(response?.data?.message || 'Published successfully');
      await fetchProfiles(pageIndex, pageSize);
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Failed to publish profiles'),
        'error'
      );
    } finally {
      setIsPublishingProfiles(false);
    }
  };

  const handleProfileStatusChange = React.useCallback(
    async (profile: LeadershipProfile, nextStatus?: LeadershipStatus) => {
      if (
        config.api.resource === 'profile' ||
        config.api.resource === 'faculty'
      ) {
        const profileId = profile.founderId ?? profile.staffId;
        if (!config.api.publish || !profileId) {
          return;
        }

        setPublishingProfileId(profile.id);

        try {
          const shouldPublish = nextStatus
            ? nextStatus === 'Publish'
            : profile.status !== 'Publish';
          const response = await httpclient().post(
            `${config.api.publish}/${profileId}?is_web_published=${shouldPublish}`
          );

          showGlobalModal(
            response?.data?.message ||
              (shouldPublish
                ? 'Published successfully'
                : 'Unpublished successfully')
          );
          await fetchProfiles(pageIndex, pageSize);
        } catch (error: any) {
          showGlobalModal(
            getApiErrorMessage(
              error,
              'Failed to update profile publish status'
            ),
            'error'
          );
        } finally {
          setPublishingProfileId(null);
        }
        return;
      }

      if (config.api.resource !== 'staff' || !profile.staffId) {
        return;
      }

      setPublishingProfileId(profile.id);

      try {
        const response = await httpclient().post('data/branch/staffPublish', {
          STAFF_IDS: [profile.staffId],
        });

        showGlobalModal(response?.data?.message || 'Published successfully');
        await fetchProfiles(pageIndex, pageSize);
      } catch (error: any) {
        showGlobalModal(
          getApiErrorMessage(error, 'Failed to publish profile'),
          'error'
        );
      } finally {
        setPublishingProfileId(null);
      }
    },
    [
      config.api.publish,
      config.api.resource,
      fetchProfiles,
      pageIndex,
      pageSize,
    ]
  );

  const profileColumns = React.useMemo<
    ManagementTableColumn<LeadershipProfile>[]
  >(
    () => [
      {
        id: 'profile',
        header: 'Profile',
        width: '6.3%',
        render: item => (
          <DMImage
            src={item.image || avatarPlaceholder}
            alt={item.facultyName}
            onError={event => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = avatarPlaceholder;
            }}
            className="h-[4.5vh] w-[4.5vh] rounded-full object-cover"
          />
        ),
      },
      {
        id: 'facultyName',
        header: config.nameColumnLabel,
        width: '16.3%',
        render: item => item.facultyName,
      },
      {
        id: 'gender',
        header: 'Gender',
        width: '8%',
        cellClassName: 'whitespace-nowrap break-normal [overflow-wrap:normal]',
        render: item => item.gender,
      },
      {
        id: 'email',
        header: 'Email',
        width: '16.4%',
        render: item => item.email,
      },
      {
        id: 'mobile',
        header: 'Mobile',
        width: '16.4%',
        render: item =>
          [item.countryCode, item.mobile].filter(Boolean).join(' '),
      },
      ...(config.showRoleColumn === false
        ? []
        : [
            {
              id: 'role',
              header: 'Role',
              width: '9.2%',
              render: item => item.role,
            },
          ]),
      {
        id: 'status',
        header: 'Status',
        width: '12%',
        cellClassName: 'whitespace-nowrap break-normal [overflow-wrap:normal]',
        render: item =>
          config.api.resource === 'profile' ||
          config.api.resource === 'faculty' ? (
            <DMSelect
              value={item.status === 'N/A' ? 'Unpublish' : item.status}
              onClick={event => event.stopPropagation()}
              onChange={event =>
                handleProfileStatusChange(
                  item,
                  event.target.value as LeadershipStatus
                )
              }
              disabled={publishingProfileId === item.id}
              className={`h-[3.4vh] min-w-[7.2vw] max-w-full cursor-pointer rounded-full border-0 py-[0.55vh] pl-[0.9vw] pr-[1.9vw] text-[1.35vh] font-semibold outline-none disabled:cursor-not-allowed disabled:opacity-60 ${
                statusClassName[
                  item.status === 'N/A' ? 'Unpublish' : item.status
                ]
              }`}
              aria-label={`Change publish status for ${item.facultyName}`}
            >
              <DMOption value="Publish">Publish</DMOption>
              <DMOption value="Unpublish">Unpublish</DMOption>
            </DMSelect>
          ) : (
            <DMText
              as="span"
              className={`rounded-full px-[0.7vw] py-[0.55vh] text-[1.35vh] font-semibold ${
                statusClassName[item.status]
              }`}
            >
              {item.status}
            </DMText>
          ),
      },
      {
        id: 'action',
        header: 'Action',
        width: '11.4%',
        cellClassName: 'px-[2vw]',
        render: item => (
          <DMDiv
            className="flex items-center gap-[1vw]"
            onClick={event => event.stopPropagation()}
          >
            <DMButton
              type="button"
              onClick={() =>
                navigate(buildEditWebsiteContentPath(config.editSubPath), {
                  state: { profile: item },
                })
              }
              className="text-primary-main"
              aria-label="Edit profile"
            >
              <Edit3 className="h-[1.8vh] w-[1.8vh]" strokeWidth={2} />
            </DMButton>
            <DMButton
              type="button"
              onClick={() => handleDeleteRequest(item)}
              disabled={deletingProfileId === item.id}
              className="text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Delete profile"
            >
              {deletingProfileId === item.id ? (
                <Loader2
                  className="h-[1.8vh] w-[1.8vh] animate-spin"
                  strokeWidth={2}
                />
              ) : (
                <Trash2 className="h-[1.8vh] w-[1.8vh]" strokeWidth={2} />
              )}
            </DMButton>
            {config.api.resource === 'staff' && (
              <DMButton
                type="button"
                onClick={() => handleProfileStatusChange(item)}
                disabled={
                  publishingProfileId === item.id || isPublishingProfiles
                }
                className="text-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Publish profile"
              >
                {publishingProfileId === item.id ? (
                  <Loader2
                    className="h-[1.9vh] w-[1.9vh] animate-spin"
                    strokeWidth={2.1}
                  />
                ) : (
                  <UploadCloud
                    className="h-[1.9vh] w-[1.9vh]"
                    strokeWidth={2.1}
                  />
                )}
              </DMButton>
            )}
          </DMDiv>
        ),
      },
    ],
    [
      config.editSubPath,
      config.nameColumnLabel,
      config.api.resource,
      config.showRoleColumn,
      deletingProfileId,
      handleDeleteRequest,
      handleProfileStatusChange,
      isPublishingProfiles,
      navigate,
      publishingProfileId,
    ]
  );

  const renderProfileSkeletonRow = (index: number) => (
    <DMDiv
      as="tr"
      key={`profile-skeleton-${index}`}
      className="h-[5.1vh] border-b border-gray-300"
    >
      <DMDiv as="td" className="border-r border-gray-300 px-[1.1vw]">
        <DMDiv className="h-[2vh] w-[2vh] animate-pulse rounded-[4px] bg-gray-200" />
      </DMDiv>
      <DMDiv as="td" className="border-r border-gray-300 px-[1.4vw]">
        <DMDiv className="h-[4.5vh] w-[4.5vh] animate-pulse rounded-full bg-gray-200" />
      </DMDiv>
      <DMDiv as="td" className="border-r border-gray-300 px-[1.9vw]">
        <DMDiv className="h-[1.8vh] w-[78%] animate-pulse rounded bg-gray-200" />
      </DMDiv>
      <DMDiv as="td" className="border-r border-gray-300 px-[1vw]">
        <DMDiv className="h-[1.8vh] w-[70%] animate-pulse rounded bg-gray-200" />
      </DMDiv>
      <DMDiv as="td" className="border-r border-gray-300 px-[1.9vw]">
        <DMDiv className="h-[1.8vh] w-[88%] animate-pulse rounded bg-gray-200" />
      </DMDiv>
      <DMDiv as="td" className="border-r border-gray-300 px-[1.9vw]">
        <DMDiv className="h-[1.8vh] w-[82%] animate-pulse rounded bg-gray-200" />
      </DMDiv>
      <DMDiv as="td" className="border-r border-gray-300 px-[1.4vw]">
        <DMDiv className="h-[1.8vh] w-[76%] animate-pulse rounded bg-gray-200" />
      </DMDiv>
      <DMDiv as="td" className="border-r border-gray-300 px-[1.1vw]">
        <DMDiv className="h-[2.6vh] w-[70%] animate-pulse rounded-full bg-gray-200" />
      </DMDiv>
      <DMDiv as="td" className="px-[2vw]">
        <DMDiv className="flex items-center gap-[1vw]">
          <DMDiv className="h-[1.9vh] w-[1.9vh] animate-pulse rounded bg-gray-200" />
          <DMDiv className="h-[1.9vh] w-[1.9vh] animate-pulse rounded bg-gray-200" />
          <DMDiv className="h-[1.9vh] w-[1.9vh] animate-pulse rounded bg-gray-200" />
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[3.2vh] font-inter text-gray-900"
    >
      <DMDiv className="mb-[1.8vh] flex shrink-0 items-start justify-between gap-[2vw]">
        <DMDiv>
          <DigitalMarketingBreadcrumb
            className="mb-[1.6vh]"
            items={[
              {
                label: 'Edit Website Content',
                path: EDIT_WEBSITE_CONTENT_BASE_PATH,
              },
              { label: config.breadcrumbLabel },
            ]}
          />
          <DMText
            as="h1"
            className="text-[3.9vh] font-bold leading-tight text-gray-950"
          >
            {config.title}
          </DMText>
          <DMText className="mt-[0.6vh] text-[1.85vh] font-medium leading-tight text-gray-600">
            {config.description}
          </DMText>
        </DMDiv>

        <DMDiv className="mt-[1vh] flex shrink-0 items-center gap-[0.8vw]">
          {(config.api.resource === 'staff' ||
            config.api.resource === 'profile' ||
            config.api.resource === 'faculty') && (
            <DMButton
              type="button"
              onClick={handlePublishProfiles}
              disabled={isPublishingProfiles || isLoadingProfiles}
              className="flex h-[7vh] items-center gap-[0.8vw] rounded-full bg-primary-main px-[1.7vw] text-[1.7vh] font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <UploadCloud className="h-[2.1vh] w-[2.1vh]" strokeWidth={2.1} />
              <DMText as="span">
                {isPublishingProfiles ? 'Publishing...' : 'Publish'}
              </DMText>
            </DMButton>
          )}
          {config.api.resource === 'staff' && (
            <ImportButton
              forceVisible
              moduleURL="staff"
              callBackFunction={() => fetchProfiles(pageIndex, pageSize)}
              height="h-[7vh]"
              width="w-auto"
              padding="px-[2vw]"
              className="!rounded-full !text-[1.7vh] !font-semibold shadow-sm transition-opacity hover:opacity-90"
            />
          )}
          <DMButton
            type="button"
            onClick={() =>
              navigate(buildEditWebsiteContentPath(config.createSubPath))
            }
            className="flex h-[7vh] items-center gap-[0.8vw] rounded-full bg-primary-main px-[2vw] text-[1.7vh] font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
          >
            <Plus className="h-[2.3vh] w-[2.3vh]" strokeWidth={2.2} />
            <DMText as="span">Add Profile</DMText>
          </DMButton>
        </DMDiv>
      </DMDiv>

      <DMDiv
        as="section"
        className="flex min-h-0 flex-1 flex-col rounded-[18px] border border-gray-300 bg-gray-50 px-[1.5vw] py-[2.4vh]"
      >
        <ManagementFilterBar
          className="mb-[2vh]"
          options={leadershipFilterOptions}
          selectedValue={statusFilter}
          allValue="all"
          onChange={handleFilterChange}
        />

        <ManagementTable
          rows={visibleProfiles}
          columns={profileColumns}
          rowKey={row => row.id}
          selectedIds={selectedIds}
          onSelectAll={handleSelectAll}
          onSelectRow={handleSelectRow}
          isRowSelectable={row =>
            (config.api.resource !== 'profile' &&
              config.api.resource !== 'faculty') ||
            row.status !== 'Publish'
          }
          getSelectLabel={row => `Select ${row.facultyName}`}
          onRowClick={setSelectedProfile}
          emptyMessage={config.emptyMessage}
          isLoading={isLoadingProfiles}
          skeletonRowCount={tableSkeletonRows.length}
          renderSkeletonRow={renderProfileSkeletonRow}
          dense
        />
        {(isLoadingProfiles || totalRecords > 0) && (
          <DMDiv className="mt-[2vh] shrink-0 [&>div]:bg-transparent">
            <PaginationBar
              key={`${config.listSubPath}-${pageSize}`}
              pageIndex={pageIndex}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              itemName="profiles"
            />
          </DMDiv>
        )}
      </DMDiv>

      <LeadershipProfileModal
        profile={selectedProfile}
        onClose={() => setSelectedProfile(null)}
        onDeleteSuccess={handleDeleteSuccess}
        onEdit={profile =>
          navigate(buildEditWebsiteContentPath(config.editSubPath), {
            state: { profile },
          })
        }
      />
      <DigitalMarketingDeleteModal
        isOpen={deleteStep === 'confirm'}
        title="Are you sure you want to delete this profile?"
        isDeleting={!!deleteProfile && deletingProfileId === deleteProfile.id}
        onCancel={() => {
          if (deletingProfileId) {
            return;
          }

          setDeleteStep('idle');
          setDeleteProfile(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </DMDiv>
  );
}
